import type { SessionProfile } from "../../shared/types/domain";
import { demoOrganizationRepository } from "../organization/demoOrganizationRepository";
import { demoEnergyLedgerAdapter } from "../incentives/demoIncentivesRepository";
import { demoNotificationAdapter } from "../governance/demoGovernanceRepository";
import type {
  CourseProduct,
  CreateOrderInput,
  PackageType,
  ProductMode,
  SalesOrder,
  SalesState,
} from "./sales.types";

const STORAGE_KEY = "boom-v4-sales";
const now = "2026-07-20T00:00:00.000Z";
const seed: SalesState = {
  products: [
    product(
      "30000000-0000-4000-8000-000000000001",
      "成人1对1·10课时",
      "one_to_one",
      "lesson_card",
      600000,
      10,
      60,
      180,
      "company",
      null,
    ),
    product(
      "30000000-0000-4000-8000-000000000002",
      "成人1对2·20课时",
      "one_to_two",
      "lesson_card",
      720000,
      20,
      60,
      240,
      "company",
      null,
    ),
    product(
      "30000000-0000-4000-8000-000000000003",
      "少儿团课·季度卡",
      "youth_group",
      "time_card",
      480000,
      24,
      90,
      90,
      "company",
      null,
    ),
  ],
  orders: [],
  payments: [],
  packages: [],
  audit: [],
};

function product(
  id: string,
  name: string,
  mode: ProductMode,
  packageType: PackageType,
  price: number,
  lessons: number,
  minutes: 60 | 90 | 120,
  duration: number,
  scope: "company" | "store",
  storeId: string | null,
): CourseProduct {
  return {
    id,
    name,
    mode,
    packageType,
    standardPriceCents: price,
    lessonCount: lessons,
    lessonMinutes: minutes,
    durationDays: duration,
    scope,
    storeId,
    active: true,
    createdAt: now,
  };
}
function clone<T>(value: T): T {
  return structuredClone(value);
}
function load(): SalesState {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return clone(seed);
  try {
    return JSON.parse(raw) as SalesState;
  } catch {
    return clone(seed);
  }
}
function save(state: SalesState) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}
function fail(message: string): never {
  throw new Error(message);
}
function audit(
  state: SalesState,
  actor: SessionProfile,
  action: string,
  entityId: string,
  beforeStatus: string | null,
  afterStatus: string,
  reason: string,
) {
  state.audit.unshift({
    id: crypto.randomUUID(),
    actorId: actor.id,
    action,
    entityId,
    beforeStatus,
    afterStatus,
    reason,
    occurredAt: new Date().toISOString(),
  });
}
async function organizationContext(actor: SessionProfile) {
  if (actor.status !== "active") fail("当前账号不是启用状态。");
  const organization = await demoOrganizationRepository.getState();
  const storedActor = organization.accounts.find(
    (item) => item.id === actor.id,
  );
  if (!storedActor || storedActor.status !== "active") fail("当前账号已停用。");
  return organization;
}
function canAccessStore(actor: SessionProfile, storeId: string) {
  return actor.role === "super" || actor.storeId === storeId;
}

export const demoSalesRepository = {
  async getState() {
    return load();
  },
  async reset() {
    localStorage.removeItem(STORAGE_KEY);
    return clone(seed);
  },
  async createProduct(
    actor: SessionProfile,
    input: {
      name: string;
      mode: ProductMode;
      packageType: PackageType;
      standardPriceYuan: number;
      lessonCount: number;
      lessonMinutes: 60 | 90 | 120;
      durationDays: number;
      scope: "company" | "store";
      storeId?: string;
    },
  ) {
    await organizationContext(actor);
    if (!["manager", "super"].includes(actor.role))
      fail("当前角色不能管理课程产品。");
    if (actor.role === "manager" && input.scope !== "store")
      fail("店长只能创建本店产品。");
    const targetStore =
      input.scope === "company"
        ? null
        : (actor.role === "manager" ? actor.storeId : input.storeId) || null;
    if (input.scope === "store" && !targetStore) fail("本店产品必须指定门店。");
    if (
      !input.name.trim() ||
      input.standardPriceYuan <= 0 ||
      input.lessonCount <= 0 ||
      input.durationDays <= 0
    )
      fail("产品字段不完整。");
    const state = load();
    const next = product(
      crypto.randomUUID(),
      input.name.trim(),
      input.mode,
      input.packageType,
      Math.round(input.standardPriceYuan * 100),
      Math.floor(input.lessonCount),
      input.lessonMinutes,
      Math.floor(input.durationDays),
      input.scope,
      targetStore,
    );
    state.products.push(next);
    audit(state, actor, "product.create", next.id, null, "active", next.name);
    save(state);
    return clone(next);
  },
  async createOrder(actor: SessionProfile, input: CreateOrderInput) {
    const organization = await organizationContext(actor);
    if (!["employee", "manager", "super"].includes(actor.role))
      fail("当前角色不能创建订单。");
    const member =
      organization.accounts.find(
        (item) =>
          item.id === input.memberId &&
          item.role === "member" &&
          item.status === "active",
      ) ?? fail("请选择启用会员。");
    if (!member.storeId || !canAccessStore(actor, member.storeId))
      fail("不能为其他门店会员创建订单。");
    const state = load();
    const selected =
      state.products.find(
        (item) =>
          item.id === input.productId &&
          item.active &&
          (item.scope === "company" || item.storeId === member.storeId),
      ) ?? fail("课程产品不可用。");
    const finalCents = Math.round(Number(input.finalPriceYuan) * 100);
    const gifts = Math.max(0, Math.floor(Number(input.giftLessons) || 0));
    if (finalCents <= 0) fail("订单实收金额必须大于0。");
    const isException = finalCents !== selected.standardPriceCents || gifts > 0;
    if (isException && !input.exceptionReason.trim())
      fail("优惠、赠课或改价必须填写原因并提交店长审批。");
    const order: SalesOrder = {
      id: crypto.randomUUID(),
      orderNumber: `V4${Date.now()}${Math.floor(Math.random() * 90 + 10)}`,
      memberId: member.id,
      storeId: member.storeId,
      salespersonId: actor.id,
      productId: selected.id,
      status: isException ? "pending_approval" : "confirmed",
      pricing: {
        productName: selected.name,
        packageType: selected.packageType,
        standardPriceCents: selected.standardPriceCents,
        finalPriceCents: finalCents,
        discountCents: selected.standardPriceCents - finalCents,
        lessonCount: selected.lessonCount,
        giftLessons: gifts,
        durationDays: selected.durationDays,
        lessonMinutes: selected.lessonMinutes,
      },
      exceptionReason: input.exceptionReason.trim(),
      approverId: null,
      approvedAt: null,
      createdAt: new Date().toISOString(),
      paidAt: null,
    };
    state.orders.unshift(order);
    audit(
      state,
      actor,
      "order.create",
      order.id,
      null,
      order.status,
      isException ? "例外价格待审批" : "标准价自动确认",
    );
    save(state);
    return clone(order);
  },
  async approveOrder(
    actor: SessionProfile,
    orderId: string,
    decision: "approve" | "reject",
    reason: string,
  ) {
    await organizationContext(actor);
    const state = load();
    const order =
      state.orders.find((item) => item.id === orderId) ?? fail("订单不存在。");
    if (
      !["manager", "super"].includes(actor.role) ||
      !canAccessStore(actor, order.storeId)
    )
      fail("当前角色不能审批该订单。");
    if (order.salespersonId === actor.id)
      fail("订单创建人不能审批自己的例外订单。");
    if (order.status !== "pending_approval") fail("只有待审批订单可以审核。");
    if (decision === "reject" && !reason.trim()) fail("驳回必须填写原因。");
    const before = order.status;
    order.status = decision === "approve" ? "confirmed" : "cancelled";
    order.approverId = actor.id;
    order.approvedAt = new Date().toISOString();
    audit(
      state,
      actor,
      decision === "approve" ? "order.approve" : "order.reject",
      order.id,
      before,
      order.status,
      reason.trim() || "价格与赠课方案确认",
    );
    save(state);
    return clone(order);
  },
  async confirmPayment(
    actor: SessionProfile,
    input: {
      orderId: string;
      method: "wechat" | "alipay" | "bank" | "cash";
      idempotencyKey: string;
    },
  ) {
    await organizationContext(actor);
    if (!["employee", "manager", "super"].includes(actor.role))
      fail("当前角色不能确认收款。");
    if (!input.idempotencyKey.trim()) fail("收款必须提供幂等键。");
    const state = load();
    const order =
      state.orders.find((item) => item.id === input.orderId) ??
      fail("订单不存在。");
    if (!canAccessStore(actor, order.storeId))
      fail("不能确认其他门店订单收款。");
    const existing = state.payments.find(
      (item) => item.idempotencyKey === input.idempotencyKey,
    );
    if (existing) {
      if (existing.orderId !== order.id) fail("幂等键已用于其他订单。");
      return {
        order: clone(order),
        payment: clone(existing),
        coursePackage: clone(
          state.packages.find((item) => item.orderId === order.id)!,
        ),
      };
    }
    if (order.status === "paid") fail("订单已经收款，不能重复确认。");
    if (order.status !== "confirmed") fail("订单未确认或未审批，不能收款。");
    const paidAt = new Date().toISOString();
    const payment = {
      id: crypto.randomUUID(),
      orderId: order.id,
      amountCents: order.pricing.finalPriceCents,
      method: input.method,
      confirmerId: actor.id,
      idempotencyKey: input.idempotencyKey,
      paidAt,
    };
    const validTo = new Date(paidAt);
    validTo.setUTCDate(validTo.getUTCDate() + order.pricing.durationDays);
    const coursePackage = {
      id: crypto.randomUUID(),
      memberId: order.memberId,
      orderId: order.id,
      coachId:
        (await demoOrganizationRepository.getState()).accounts.find(
          (account) => account.id === order.memberId,
        )?.coachId ?? order.salespersonId,
      beneficiaryName:
        (await demoOrganizationRepository.getState()).accounts.find(
          (account) => account.id === order.memberId,
        )?.name ?? "会员",
      productName: order.pricing.productName,
      packageType: order.pricing.packageType,
      totalUnits: order.pricing.lessonCount + order.pricing.giftLessons,
      usedUnits: 0,
      purchasedUnits: order.pricing.lessonCount,
      giftUnits: order.pricing.giftLessons,
      openingUsedUnits: 0,
      validFrom: paidAt,
      validTo: validTo.toISOString(),
      status: "active" as const,
      sourceType: "purchase" as const,
      validityOverride: false,
    };
    const before = order.status;
    order.status = "paid";
    order.paidAt = paidAt;
    state.payments.push(payment);
    state.packages.push(coursePackage);
    audit(
      state,
      actor,
      "payment.confirm",
      order.id,
      before,
      "paid",
      `${input.method}收款并开通课包`,
    );
    save(state);
    const salesperson = await demoOrganizationRepository
      .getState()
      .then((organization) =>
        organization.accounts.find((item) => item.id === order.salespersonId),
      );
    if (salesperson?.role === "employee")
      demoEnergyLedgerAdapter.awardAutomatic(
        salesperson.id,
        order.storeId,
        "RENEWAL_SUPPORT",
        payment.id,
      );
    demoNotificationAdapter.notify({
      recipientId: order.memberId,
      storeId: order.storeId,
      type: "system",
      title: "课程包已开通",
      content: "门店已确认收款，课程包已经生效。",
      entityType: "sales_order",
      entityId: order.id,
      dedupeKey: `sales_order:${order.id}:system`,
    });
    return {
      order: clone(order),
      payment: clone(payment),
      coursePackage: clone(coursePackage),
    };
  },
};

export const demoPackageLedgerAdapter = {
  async getPackage(packageId: string) {
    return clone(load().packages.find((item) => item.id === packageId) ?? null);
  },
  async consume(packageId: string, units: number) {
    const state = load();
    const target =
      state.packages.find((item) => item.id === packageId) ??
      fail("课程包不存在。");
    if (target.status !== "active") fail("课程包不可用。");
    if (units <= 0 || target.usedUnits + units > target.totalUnits)
      fail("课程包剩余课时不足。");
    target.usedUnits += units;
    save(state);
    return clone(target);
  },
  async reverse(packageId: string, units: number) {
    const state = load();
    const target =
      state.packages.find((item) => item.id === packageId) ??
      fail("课程包不存在。");
    if (units <= 0 || target.usedUnits - units < 0) fail("反向课时流水异常。");
    target.usedUnits -= units;
    save(state);
    return clone(target);
  },
};

export const demoSalesRefundAdapter = {
  async getContext(orderId: string) {
    const state = load();
    const order = state.orders.find((x) => x.id === orderId) ?? null;
    if (!order) return null;
    return clone({
      order,
      payment: state.payments.find((x) => x.orderId === orderId) ?? null,
      coursePackage: state.packages.find((x) => x.orderId === orderId) ?? null,
    });
  },
  async applyRefund(orderId: string, units: number) {
    const state = load();
    const order =
      state.orders.find((x) => x.id === orderId) ?? fail("订单不存在。");
    const target =
      state.packages.find((x) => x.orderId === orderId) ??
      fail("课程包不存在。");
    if (units <= 0 || target.totalUnits - units < target.usedUnits)
      fail("退款课时超过未使用课时。");
    target.totalUnits -= units;
    if (target.totalUnits === target.usedUnits) {
      target.status = "closed";
      order.status = "refunded";
    } else order.status = "partially_refunded";
    save(state);
    return clone({ order, coursePackage: target });
  },
};
